import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton as MuiIconButton,
    Box,
    Button,
    Slider,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

type Props = {
    open: boolean;
    imageSrc: string | null;
    aspect?: number;
    onClose: () => void;
    onCropComplete: (croppedBlob: Blob) => void;
};

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas toBlob failed'));
        }, 'image/jpeg', 0.92);
    });
}

function createImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', (err) => reject(err));
        img.crossOrigin = 'anonymous';
        img.src = url;
    });
}

export const ImageCropDialog: React.FC<Props> = ({
    open,
    imageSrc,
    aspect = 16 / 9,
    onClose,
    onCropComplete,
}) => {
    const { t } = useTranslation();
    const [crop, setCrop] = React.useState({ x: 0, y: 0 });
    const [zoom, setZoom] = React.useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null);

    React.useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
        }
    }, [open]);

    const handleCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleConfirm = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        onCropComplete(croppedBlob);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
                paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {t('locationsManagement.create.cropImage')}
                <MuiIconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </MuiIconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ position: 'relative', width: '100%', height: 350, backgroundColor: '#333' }}>
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                        />
                    )}
                </Box>
                <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', minWidth: 40 }}>
                        Zoom
                    </Typography>
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(_, value) => setZoom(value as number)}
                        sx={{ color: 'var(--color-primary)' }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        borderRadius: 0,
                        borderColor: 'var(--color-error)',
                        color: 'var(--color-error)',
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                        borderRadius: 0,
                        backgroundColor: 'var(--color-primary)',
                        boxShadow: 'none',
                        '&:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'var(--color-primary-hover)',
                            boxShadow: 'var(--shadow-1)',
                        },
                    }}
                >
                    {t('common.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
